import React from "react";
import {useShallow} from "zustand/react/shallow";
import {useListingsStore} from "../../stores/listingsStore";
import {activeFilterChips, clearedSubFilters} from "./utils";

// Ribbon of removable chips for the filters selected OUTSIDE the search bar (the
// left-side sidebar facets). Year/Make/Model stay in the search bar and are never
// shown here. Chips are a pure derivation of the store's `filters` (via
// activeFilterChips) — no separate state — so a chip and its sidebar control always
// agree. Removing a chip issues the SAME setFilters call the sidebar checkbox does,
// so the sidebar option unchecks and the listing refresh/pagination-reset are
// identical. Hidden entirely when nothing eligible is selected.
export function ActiveFiltersBar() {
    const {filters, options, setFilters} = useListingsStore(
        useShallow((state) => ({
            filters: state.filters,
            options: state.options,
            setFilters: state.setFilters,
        }))
    )

    const chips = activeFilterChips(filters, options)
    if (chips.length === 0) return null

    // Remove one value from its multi-select field. `toggledField` = the field, so
    // the store skips/keeps that facet's own (self-excluded) counts — byte-for-byte
    // the sidebar toggle. Not the seat "keep at least one" guard: a ✕ always removes.
    const removeChip = (chip) => {
        const next = (filters[chip.field] || []).filter((v) => v !== chip.value)
        setFilters({[chip.field]: next}, false, chip.field)
    }

    // One coordinated update clears every ribbon field (search-bar Year/Make/Model,
    // sort, view, zip, radius and dealer scope are deliberately untouched). A single
    // request; shouldResetOptions=true because the cohort changes for every facet.
    // clearedSubFilters() is the shared payload the search bar's cascade also uses —
    // same fields, same values, one definition.
    const clearAll = () => setFilters(clearedSubFilters(), true)

    return (
        <div className="active-filters-bar">
            <div className="active-filters-inner">
                {chips.map((chip) => (
                    <span key={chip.key} className="filter-chip">
                        <span className="filter-chip-label">{chip.label}</span>
                        <button
                            type="button"
                            className="filter-chip-remove"
                            aria-label={`Remove ${chip.label} filter`}
                            onClick={() => removeChip(chip)}
                        >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                                 stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
                                <path d="M6 6l12 12M18 6L6 18"/>
                            </svg>
                        </button>
                    </span>
                ))}
                <button type="button" className="filter-chip-clear-all" onClick={clearAll}>
                    Clear all
                </button>
            </div>
        </div>
    );
}
